/**
 * Projects Service
 * 
 * Handles project CRUD operations and membership management.
 * Projects are the top-level containers for all story entities.
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  collectionGroup,
} from 'firebase/firestore'
import { db } from '../firebase'

// Collection names
export const PROJECTS_COLLECTION = 'projects'
export const MEMBERS_SUBCOLLECTION = 'members'

// Role types
export const PROJECT_ROLES = {
  ADMIN: 'admin',
  GUEST: 'guest',
  CUSTOM: 'custom',
}

// ============================================
// Project CRUD Operations
// ============================================

/**
 * Create a new project
 * Automatically adds the creator as an admin member
 * 
 * @param {string} userId - The ID of the user creating the project
 * @param {object} projectData - Project data (name, description)
 * @returns {Promise<string>} - The new project ID
 */
export async function createProject(userId, projectData) {
  if (!userId) {
    throw new Error('User ID is required to create a project')
  }

  // Create the project document
  const projectRef = await addDoc(collection(db, PROJECTS_COLLECTION), {
    name: projectData.name || 'Untitled Project',
    description: projectData.description || '',
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })

  // Add creator as admin member
  await setDoc(doc(db, PROJECTS_COLLECTION, projectRef.id, MEMBERS_SUBCOLLECTION, userId), {
    role: PROJECT_ROLES.ADMIN,
    addedAt: serverTimestamp(),
    addedBy: userId,
  })

  return projectRef.id
}

/**
 * Get a single project by ID
 * 
 * @param {string} projectId - The project ID
 * @returns {Promise<object|null>} - The project data or null if not found
 */
export async function getProject(projectId) {
  if (!projectId) {
    throw new Error('Project ID is required')
  }

  const projectRef = doc(db, PROJECTS_COLLECTION, projectId)
  const projectSnap = await getDoc(projectRef)

  if (projectSnap.exists()) {
    return { id: projectSnap.id, ...projectSnap.data() }
  }
  return null
}

/**
 * Update a project
 * 
 * @param {string} projectId - The project ID
 * @param {object} data - Fields to update (name, description)
 * @returns {Promise<void>}
 */
export async function updateProject(projectId, data) {
  if (!projectId) {
    throw new Error('Project ID is required')
  }

  const projectRef = doc(db, PROJECTS_COLLECTION, projectId)
  
  // Only allow updating specific fields
  const allowedFields = ['name', 'description']
  const updateData = {}
  
  for (const field of allowedFields) {
    if (data[field] !== undefined) {
      updateData[field] = data[field]
    }
  }

  await updateDoc(projectRef, {
    ...updateData,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Delete a project
 * Note: This only deletes the project document. 
 * Subcollections (members, entities) should be handled separately or via Cloud Functions.
 * 
 * @param {string} projectId - The project ID
 * @returns {Promise<void>}
 */
export async function deleteProject(projectId) {
  if (!projectId) {
    throw new Error('Project ID is required')
  }

  const projectRef = doc(db, PROJECTS_COLLECTION, projectId)
  await deleteDoc(projectRef)
}

// ============================================
// Membership Management
// ============================================

/**
 * Add a member to a project
 * 
 * @param {string} projectId - The project ID
 * @param {string} userId - The user ID to add
 * @param {string} role - The role to assign (admin, guest, custom)
 * @param {string} addedBy - The user ID of who is adding this member
 * @returns {Promise<void>}
 */
export async function addProjectMember(projectId, userId, role, addedBy) {
  if (!projectId || !userId) {
    throw new Error('Project ID and User ID are required')
  }

  if (!Object.values(PROJECT_ROLES).includes(role)) {
    throw new Error(`Invalid role: ${role}. Must be one of: ${Object.values(PROJECT_ROLES).join(', ')}`)
  }

  const memberRef = doc(db, PROJECTS_COLLECTION, projectId, MEMBERS_SUBCOLLECTION, userId)
  
  await setDoc(memberRef, {
    role,
    addedAt: serverTimestamp(),
    addedBy: addedBy || userId,
  })
}

/**
 * Update a project member's role
 * 
 * @param {string} projectId - The project ID
 * @param {string} userId - The user ID to update
 * @param {string} newRole - The new role to assign
 * @returns {Promise<void>}
 */
export async function updateProjectMemberRole(projectId, userId, newRole) {
  if (!projectId || !userId) {
    throw new Error('Project ID and User ID are required')
  }

  if (!Object.values(PROJECT_ROLES).includes(newRole)) {
    throw new Error(`Invalid role: ${newRole}. Must be one of: ${Object.values(PROJECT_ROLES).join(', ')}`)
  }

  const memberRef = doc(db, PROJECTS_COLLECTION, projectId, MEMBERS_SUBCOLLECTION, userId)
  
  await updateDoc(memberRef, {
    role: newRole,
  })
}

/**
 * Remove a member from a project
 * 
 * @param {string} projectId - The project ID
 * @param {string} userId - The user ID to remove
 * @returns {Promise<void>}
 */
export async function removeProjectMember(projectId, userId) {
  if (!projectId || !userId) {
    throw new Error('Project ID and User ID are required')
  }

  const memberRef = doc(db, PROJECTS_COLLECTION, projectId, MEMBERS_SUBCOLLECTION, userId)
  await deleteDoc(memberRef)
}

/**
 * Get a single project member
 * 
 * @param {string} projectId - The project ID
 * @param {string} userId - The user ID
 * @returns {Promise<object|null>} - The membership data or null
 */
export async function getProjectMember(projectId, userId) {
  if (!projectId || !userId) {
    throw new Error('Project ID and User ID are required')
  }

  const memberRef = doc(db, PROJECTS_COLLECTION, projectId, MEMBERS_SUBCOLLECTION, userId)
  const memberSnap = await getDoc(memberRef)

  if (memberSnap.exists()) {
    return { userId: memberSnap.id, ...memberSnap.data() }
  }
  return null
}

/**
 * Get all members of a project
 * 
 * @param {string} projectId - The project ID
 * @returns {Promise<array>} - Array of membership objects
 */
export async function getProjectMembers(projectId) {
  if (!projectId) {
    throw new Error('Project ID is required')
  }

  const membersRef = collection(db, PROJECTS_COLLECTION, projectId, MEMBERS_SUBCOLLECTION)
  const membersSnap = await getDocs(membersRef)

  return membersSnap.docs.map(doc => ({
    userId: doc.id,
    ...doc.data(),
  }))
}

// ============================================
// User Project Queries
// ============================================

/**
 * Get all projects where a user is a member
 * Uses collection group query to search across all members subcollections
 * 
 * Note: This requires a Firestore index on the members collection group
 * 
 * @param {string} userId - The user ID
 * @returns {Promise<array>} - Array of projects with user's role
 */
export async function getUserProjects(userId) {
  if (!userId) {
    throw new Error('User ID is required')
  }

  // Query all members subcollections for documents with the user's ID
  // This requires the document ID to match the userId, which we set in addProjectMember
  const membersGroupRef = collectionGroup(db, MEMBERS_SUBCOLLECTION)
  
  // We need to fetch all membership docs and filter by ID client-side
  // because Firestore doesn't allow querying by document ID in collection groups
  const membershipsSnap = await getDocs(membersGroupRef)
  
  // Filter to only this user's memberships
  const userMemberships = membershipsSnap.docs.filter(doc => doc.id === userId)
  
  // Extract project IDs from the document paths
  // Path format: projects/{projectId}/members/{userId}
  const projectPromises = userMemberships.map(async (memberDoc) => {
    const pathParts = memberDoc.ref.path.split('/')
    const projectId = pathParts[1] // projects/{projectId}/members/{userId}
    
    const project = await getProject(projectId)
    if (project) {
      return {
        ...project,
        userRole: memberDoc.data().role,
      }
    }
    return null
  })

  const projects = await Promise.all(projectPromises)
  
  // Filter out nulls (deleted projects) and sort by creation date
  return projects
    .filter(Boolean)
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0
      const bTime = b.createdAt?.toMillis?.() || 0
      return bTime - aTime // Newest first
    })
}

/**
 * Check if a user has access to a project
 * 
 * @param {string} projectId - The project ID
 * @param {string} userId - The user ID
 * @returns {Promise<boolean>} - Whether the user is a member
 */
export async function userHasProjectAccess(projectId, userId) {
  const member = await getProjectMember(projectId, userId)
  return member !== null
}

/**
 * Get a user's role in a project
 * 
 * @param {string} projectId - The project ID
 * @param {string} userId - The user ID
 * @returns {Promise<string|null>} - The user's role or null if not a member
 */
export async function getUserProjectRole(projectId, userId) {
  const member = await getProjectMember(projectId, userId)
  return member?.role || null
}
